<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import CardTaskRow from './CardTaskRow.vue';
import CardTaskAnswers from './CardTaskAnswers.vue';
import CardVisitSteps from './CardVisitSteps.vue';
import { OPEN_TASK_STATUSES } from '../cardStatus';
import { isVisitRequest, shownTask, topLevelTasks } from '../visitTask';

const props = defineProps({
  tasks: { type: Array, required: true },
  agents: { type: Array, required: true },
  // Whether "Nova tarefa" also offers to schedule a visit (the account's visit type is active).
  canScheduleVisit: { type: Boolean, default: false },
  // Resolves to whether the task was deleted; see useCardDetail.
  deleteTask: { type: Function, required: true },
});

// `status` and `update` name the task they apply to (a visit request's may be its visit);
// `schedule` asks to schedule a visit request; `scheduleVisit` to schedule a visit on the card.
const emit = defineEmits([
  'create',
  'status',
  'update',
  'schedule',
  'scheduleVisit',
]);

const { t } = useI18n();
const isMenuOpen = ref(false);

const newTaskItems = computed(() => [
  {
    label: t('CARDS.TASKS.NEW_TASK'),
    value: 'create',
    action: 'create',
    icon: 'i-lucide-list-todo',
  },
  {
    label: t('CARDS.VISIT.SCHEDULE'),
    value: 'scheduleVisit',
    action: 'scheduleVisit',
    icon: 'i-lucide-calendar-plus',
  },
]);

const onNew = () => {
  if (props.canScheduleVisit) isMenuOpen.value = !isMenuOpen.value;
  else emit('create');
};

const onNewItem = ({ action }) => {
  isMenuOpen.value = false;
  emit(action);
};

// The tasks opened, each showing its answers right under its row.
const expandedIds = defineModel('expandedIds', {
  type: Array,
  default: () => [],
});

const isExpanded = task => expandedIds.value.includes(task.id);

const toggle = task => {
  expandedIds.value = isExpanded(task)
    ? expandedIds.value.filter(id => id !== task.id)
    : [...expandedIds.value, task.id];
};

// A visit request is as open as the visit it shows.
const isOpen = task => OPEN_TASK_STATUSES.includes(shownTask(task).status);

const listedTasks = computed(() => topLevelTasks(props.tasks));

// A deletion is confirmed first; the tasks scheduled out of the deleted one stay.
const deleteDialogRef = ref(null);
const deleting = ref(null);

const askDelete = task => {
  deleting.value = task;
  deleteDialogRef.value.open();
};

const confirmDelete = async () => {
  if (!(await props.deleteTask(deleting.value.id))) return;
  deleteDialogRef.value.close();
  useAlert(t('CARDS.TASKS.DELETED'));
};

// The work still to do first, each group in the order it was asked for.
const orderedTasks = computed(() => [
  ...listedTasks.value.filter(isOpen),
  ...listedTasks.value.filter(task => !isOpen(task)),
]);

const openCount = computed(() => listedTasks.value.filter(isOpen).length);
</script>

<template>
  <section class="flex flex-col gap-3">
    <div class="flex items-center gap-2">
      <h2 class="mb-0 text-sm font-medium text-n-slate-12">
        {{ $t('CARDS.TASKS.TITLE') }}
      </h2>
      <span
        v-if="tasks.length"
        class="text-xs text-n-slate-10"
        data-testid="tasks-count"
      >
        {{
          $t('CARDS.TASKS.COUNT', {
            open: openCount,
            total: listedTasks.length,
          })
        }}
      </span>
      <div
        v-on-clickaway="() => (isMenuOpen = false)"
        class="relative ltr:ml-auto rtl:mr-auto"
      >
        <Button
          :label="$t('CARDS.TASKS.NEW')"
          icon="i-lucide-plus"
          size="sm"
          variant="ghost"
          color="slate"
          data-testid="task-new"
          @click="onNew"
        />
        <DropdownMenu
          v-if="isMenuOpen"
          :menu-items="newTaskItems"
          class="z-50 w-44 mt-1 top-full ltr:right-0 rtl:left-0"
          data-testid="task-new-menu"
          @action="onNewItem"
        />
      </div>
    </div>
    <ul
      v-if="tasks.length"
      class="flex flex-col mb-0 list-none border divide-y ltr:pl-0 rtl:pr-0 rounded-xl border-n-weak divide-n-weak"
    >
      <li v-for="task in orderedTasks" :key="task.id">
        <!-- The whole row toggles the task; the pickers inside it stop the click. -->
        <div
          role="button"
          tabindex="0"
          class="flex flex-wrap items-center w-full gap-x-3 gap-y-2 px-3 py-2.5 text-left cursor-pointer"
          :data-task-id="task.id"
          :aria-expanded="isExpanded(task)"
          @click="toggle(task)"
          @keydown.enter.self="toggle(task)"
        >
          <CardTaskRow
            :task="task"
            :agents="agents"
            @update="(target, attributes) => emit('update', target, attributes)"
            @status="(target, transition) => emit('status', target, transition)"
            @schedule="emit('schedule', task)"
            @delete="askDelete"
            deletable
          />
          <span
            class="i-lucide-chevron-down size-4 text-n-slate-10 transition-transform"
            :class="{ 'rotate-180': isExpanded(task) }"
          />
        </div>
        <div
          v-if="isExpanded(task)"
          class="flex flex-col gap-3 px-3 pt-3 pb-4 border-t border-n-weak"
          :data-task-expanded="task.id"
        >
          <CardVisitSteps v-if="isVisitRequest(task)" :task="task" />
          <h3 class="mb-0 text-sm font-medium text-n-slate-12">
            {{ $t('CARDS.TASKS.ANSWERS') }}
          </h3>
          <CardTaskAnswers
            :fields="task.fields"
            :data="task.data"
            revealable
          />
        </div>
      </li>
    </ul>
    <p v-else class="mb-0 text-sm text-n-slate-10">
      {{ $t('CARDS.TASKS.EMPTY') }}
    </p>
    <Dialog
      ref="deleteDialogRef"
      type="alert"
      :title="$t('CARDS.TASKS.DELETE_TITLE', { title: deleting?.title })"
      :description="$t('CARDS.TASKS.DELETE_DESCRIPTION')"
      :confirm-button-label="$t('CARDS.TASKS.DELETE')"
      @confirm="confirmDelete"
    />
  </section>
</template>
